import { createTheme, css } from '@mui/material/styles'
import { unifiedColors } from 'styles/colors'
import { base, hide, media } from './responsive'
import type {} from '@mui/x-data-grid/themeAugmentation'
import type {} from '@mui/x-data-grid-pro/themeAugmentation'

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    lg: true
    xl: true
    xxl: true
  }

  interface TypographyVariants {
    hero: React.CSSProperties
    heroAlt: React.CSSProperties
    h1Alt: React.CSSProperties
    h2Alt: React.CSSProperties
    h3Alt: React.CSSProperties
    h4Alt: React.CSSProperties
    h5Alt: React.CSSProperties
    bodyLBold: React.CSSProperties
    bodyLSemiBold: React.CSSProperties
    bodyLRegular: React.CSSProperties
    bodyLRegularUnderline: React.CSSProperties
    bodyMBold: React.CSSProperties
    bodyMSemiBold: React.CSSProperties
    bodyMRegular: React.CSSProperties
    bodyMRegularUnderline: React.CSSProperties
    bodySBold: React.CSSProperties
    bodySSemiBold: React.CSSProperties
    bodySRegular: React.CSSProperties
    bodySRegularUnderline: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    hero: React.CSSProperties
    heroAlt: React.CSSProperties
    h1Alt: React.CSSProperties
    h2Alt: React.CSSProperties
    h3Alt: React.CSSProperties
    h4Alt: React.CSSProperties
    h5Alt: React.CSSProperties
    bodyLBold: React.CSSProperties
    bodyLSemiBold: React.CSSProperties
    bodyLRegular: React.CSSProperties
    bodyLRegularUnderline: React.CSSProperties
    bodyMBold: React.CSSProperties
    bodyMSemiBold: React.CSSProperties
    bodyMRegular: React.CSSProperties
    bodyMRegularUnderline: React.CSSProperties
    bodySBold: React.CSSProperties
    bodySSemiBold: React.CSSProperties
    bodySRegular: React.CSSProperties
    bodySRegularUnderline: React.CSSProperties
  }

  type UnifiedColors = typeof unifiedColors

  interface Palette extends UnifiedColors {
    tertiary: Palette['primary']
    quaternary: Palette['primary']
  }

  interface PaletteOptions extends UnifiedColors {
    tertiary?: PaletteOptions['primary']
    quaternary?: PaletteOptions['primary']
  }

  interface Mixins {
    MuiDataGrid: {
      pinnedBackground: string
      containerBackground: string
    }
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    hero: true
    heroAlt: true
    h1Alt: true
    h2Alt: true
    h3Alt: true
    h4Alt: true
    h5Alt: true
    bodyLBold: true
    bodyLSemiBold: true
    bodyLRegular: true
    bodyLRegularUnderline: true
    bodyMBold: true
    bodyMSemiBold: true
    bodyMRegular: true
    bodyMRegularUnderline: true
    bodySBold: true
    bodySSemiBold: true
    bodySRegular: true
    bodySRegularUnderline: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true
    quaternary: true
  }
}

const fontWeights = {
  regular: 500,
  semiBold: 600,
  large: 700
}

export const fontFamilies = {
  titleFont: 'var(--font-molitor-italic), serif',
  titleFontAlt: 'var(--font-molitor-bold), serif',
  bodyFont: 'var(--font-name-sans), serif'
}

const mixins = {
  flexCenter: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `
}

export type Theme = typeof theme

const headingBase: React.CSSProperties = {
  fontWeight: fontWeights.large,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  color: unifiedColors.label.default
}

const heading: React.CSSProperties = {
  ...headingBase,
  fontFamily: fontFamilies.titleFont
}

const headingAlt: React.CSSProperties = {
  ...headingBase,
  fontFamily: fontFamilies.titleFontAlt
}

const bodyBase: React.CSSProperties = {
  lineHeight: 1.3,
  fontFamily: fontFamilies.bodyFont,
  fontWeight: fontWeights.regular,
  color: unifiedColors.label.default
}

export const theme = createTheme({
  breakpoints: {
    values: { ...base }
  },
  typography: {
    fontFamily: fontFamilies.bodyFont,
    hero: {
      ...heading,
      fontSize: 110
    },
    heroAlt: {
      ...headingAlt,
      fontSize: 110
    },
    h1: {
      ...heading,
      fontSize: 56
    },
    h1Alt: {
      ...headingAlt,
      fontSize: 56
    },
    h2: {
      ...heading,
      fontSize: 32
    },
    h2Alt: {
      ...headingAlt,
      fontSize: 32
    },
    h3: {
      ...heading,
      fontSize: 26
    },
    h3Alt: {
      ...headingAlt,
      fontSize: 26
    },
    h4: {
      ...heading,
      fontSize: 22
    },
    h4Alt: {
      ...headingAlt,
      fontSize: 22
    },
    h5: {
      ...heading,
      fontSize: 19
    },
    h5Alt: {
      ...headingAlt,
      fontSize: 19
    },
    h6: {
      fontSize: 18,
      fontWeight: fontWeights.large,
      lineHeight: 1.2
    },
    body1: {
      ...bodyBase,
      fontSize: 16
    },
    body2: {
      ...bodyBase,
      fontSize: 14
    },
    bodyLBold: {
      ...bodyBase,
      fontSize: 16,
      fontWeight: fontWeights.large
    },
    bodyLSemiBold: {
      ...bodyBase,
      fontSize: 16,
      fontWeight: fontWeights.semiBold
    },
    bodyLRegular: {
      ...bodyBase,
      fontSize: 16
    },
    bodyLRegularUnderline: {
      ...bodyBase,
      fontSize: 16,
      textDecoration: 'underline'
    },
    bodyMBold: {
      ...bodyBase,
      fontSize: 14,
      fontWeight: fontWeights.large
    },
    bodyMSemiBold: {
      ...bodyBase,
      fontSize: 14,
      fontWeight: fontWeights.semiBold
    },
    bodyMRegular: {
      ...bodyBase,
      fontSize: 14
    },
    bodyMRegularUnderline: {
      ...bodyBase,
      fontSize: 14,
      textDecoration: 'underline'
    },
    bodySBold: {
      ...bodyBase,
      fontSize: 12,
      fontWeight: fontWeights.large
    },
    bodySSemiBold: {
      ...bodyBase,
      fontSize: 12,
      fontWeight: fontWeights.semiBold
    },
    bodySRegular: {
      ...bodyBase,
      fontSize: 12
    },
    bodySRegularUnderline: {
      ...bodyBase,
      fontSize: 12,
      textDecoration: 'underline'
    }
  },
  components: {
    MuiTypography: {
      defaultProps: { variantMapping: { hero: 'h1' } }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { flex: 'none' }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        // The props to change the default for.
        disableRipple: true
      }
    },
    MuiButton: {
      defaultProps: { variant: 'contained', disableElevation: true },
      styleOverrides: {
        root: () => {
          return {
            textTransform: 'none',
            color: unifiedColors.label.default,
            backgroundColor: unifiedColors.button.tertiary.default,
            '&:hover': {
              backgroundColor: unifiedColors.button.tertiary.hovered
            }
          }
        }
      }
    }
  },
  palette: {
    background: { default: unifiedColors.surface.secondary },
    primary: { main: unifiedColors.button.primary.default },
    secondary: { main: unifiedColors.button.secondary.default },
    tertiary: { main: unifiedColors.button.tertiary.default },
    quaternary: { main: unifiedColors.button.quaternary.default },
    button: unifiedColors.button,
    label: unifiedColors.label,
    shadow: unifiedColors.shadow,
    surface: unifiedColors.surface,
    icon: unifiedColors.icon,
    border: unifiedColors.border
  },
  shape: { borderRadius: 12 },

  mixins: {
    MuiDataGrid: {
      // Pinned columns sections
      pinnedBackground: unifiedColors.surface.default,
      // Headers, and top & bottom fixed rows
      containerBackground: unifiedColors.surface.default
    }
  }
})

export const themeUtils = {
  ...fontFamilies,
  ...mixins,
  ...media,
  ...hide,
  fontWeights,
  hero: createVariant('hero'),
  body: css`
    ${createVariant('bodyLRegular')}
    ${media.maxWidth.sm} {
      ${createVariant('bodyMRegular')}
    }
  `,
  label: createVariant('bodyMBold'),
  // REGULAR
  bodyBold: createVariant('bodyLBold'),
  bodyBoldGrey: createVariant('bodyLBold', 'subdued'),
  // SMALL
  bodySmall: createVariant('bodyMRegular'),
  bodySmallBold: createVariant('bodyMBold'),
  bodySmallGrey: createVariant('bodyMRegular', 'subdued'),
  bodySmallBoldGrey: createVariant('bodyMBold', 'subdued'),
  bodySmallSemiBold: createVariant('bodyMSemiBold'),
  bodySmallSemiBoldGrey: createVariant('bodyMSemiBold', 'subdued'),
  bodySmallWhite: createVariant('bodyMRegular', 'defaultDark'),
  // EXTRA SMALL
  bodyXSmall: createVariant('bodySRegular'),
  bodyXSmallBold: createVariant('bodySBold'),
  bodyXSmallGrey: createVariant('bodySRegular', 'subdued'),
  bodyXSmallWhite: createVariant('bodySRegular', 'defaultDark'),
  bodyXSmallSemiBold: createVariant('bodySSemiBold'),
  bodyXSmallSemiBoldGrey: createVariant('bodySSemiBold', 'subdued'),
  button: createVariant('bodyLBold'),
  h1: createVariant('h1'),
  h2: createVariant('h2'),
  h3: createVariant('h3'),
  h4: createVariant('h4'),
  h5: createVariant('h5'),
  h6: createVariant('h6')
}

function createVariant(
  typographyKey: keyof typeof theme.typography,
  colorKey?: keyof typeof theme.palette.label
) {
  return css({
    ...(theme.typography[typographyKey] as React.CSSProperties),
    ...(colorKey ? { color: theme.palette.label[colorKey] } : {})
  })
}
